import { Intent } from "@blueprintjs/core";
import axios from "axios";
import classNames from "classnames";
import { Button, ButtonSave } from "components/buttons";
import { FormTextInput } from "components/form-fields";
import { ShowHide } from "components/layout";
import { AlertConfirm, NotificationInline, NotificationToaster } from "components/notifications";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { MfaService } from "services";
import { authentication } from 'components/user-access';

export function UserMfaSettings({ userId, editingOwnAccount = false }) {
    const [mfaSettings, setMfaSettings] = useState({
        mfaIdentifier: "",
        mfaEnforced: false,
        allowedMfaTypes: [],
        activeMfaType: ""
    });

    const [mfaTypes, setMfaTypes] = useState([]);
    const [isResettingMfa, setIsResettingMfa] = useState(false);
    const [isSettingUpMfa, setIsSettingUpMfa] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const [showMfaResetSuccess, setShowMfaResetSuccess] = useState(false);

    const getMfaSettings = () => editingOwnAccount ? MfaService.getCurrentUserMfaSettings() : MfaService.getUserMfaSettings(userId);
    const callApiToResetMfa = () => editingOwnAccount ? MfaService.resetMfaForCurrentUser() : MfaService.resetMfaForUser(userId);

    useEffect(() => {
        axios.all([getMfaSettings(), MfaService.getMfaTypes()]).then(
            ([mfaSettingsResponse, mfaTypesResponse]) => {
                setMfaSettings(mfaSettingsResponse);
                setMfaTypes(mfaTypesResponse.mfaTypes);
                setLoading(false);
            }
        );
    }, [userId, editingOwnAccount]);

    const activeMfaType = mfaSettings.activeMfaType && mfaTypes.length ? mfaTypes.find(t => t.key === mfaSettings.activeMfaType).name : '';

    const resetMfa = () => {
        setSaving(true);
        callApiToResetMfa().then(() => {
            setIsResettingMfa(false);
            setMfaSettings({
                ...mfaSettings,
                activeMfaType: ""
            });
            setSaving(false);
            setIsResettingMfa(false);

            setShowMfaResetSuccess(true);
        }, () => {
            setSaving(false);
            setIsResettingMfa(false);
            NotificationToaster.show(Intent.DANGER, `There was an error resetting MFA for ${editingOwnAccount ? "your account" : "this user"}. Please try again.`);
        });
    }

    const setupMfa = () => {
        setIsSettingUpMfa(false);
        handleReLogin();
    }

    const handleReLogin = () => {        
        authentication.forceLogin({ forceMfa: "true" });
    };

    const warningWhenResettingMfaOnEnforcedAccount = `Resetting MFA will require ${editingOwnAccount ? "you" : "the user"} to set it up again when next logging in.`;
    const warningWhenResettingMfaOnUndenforcedAccount = `Resetting MFA will remove the currently active MFA method, but ${editingOwnAccount ? "you" : "the user"} will not be required to set up MFA again until ${editingOwnAccount ? "you" : "they"} choose to enable it.`;

    const descriptionWhenMfaIsNotActive = "Here you can setup MFA for your user. MFA adds an additional layer of security to your account by requiring you to provide a second form of verification when logging in, such as a code from an authenticator app or a text message.";
    const descriptionWhenMfaIsActive = `Here you can see what MFA is enabled for ${editingOwnAccount ? "your account" : "this user"}.`;

    return (
        <div className="row">
            <h2 >Multi-Factor Authentication (MFA)</h2>
            <div className="intro-text">
                <p className={classNames({ 'bp6-skeleton': loading })}>{activeMfaType ? descriptionWhenMfaIsActive : descriptionWhenMfaIsNotActive}</p>
            </div>
            <NotificationInline intent={Intent.SUCCESS} show={showMfaResetSuccess} allowClose={false} text={`MFA has been reset for ${editingOwnAccount ? "your account" : "this user"}.`} />
            <ShowHide
                evaluator={!!activeMfaType}
                show={<FormTextInput
                    headingText="Active MFA method"
                    value={activeMfaType}
                    loading={loading}
                    disabled
                />}
            />

            <ShowHide
                evaluator={mfaSettings.activeMfaType === 'SMS'}
                show={<FormTextInput headingText="Phone number used" value={mfaSettings.mfaIdentifier} loading={loading} disabled />}
            />
            <div className="button-row">
                <ShowHide
                    evaluator={!!mfaSettings.activeMfaType}
                    show={<ButtonSave
                        text="Reset Muti-Factor Authentication"
                        onClick={() => setIsResettingMfa(true)}
                        simpleDisabled={!mfaSettings.activeMfaType}
                        disabled={saving}
                        loading={loading}
                        icon="reset"
                        intent={Intent.DANGER}
                    />}
                />
                <ShowHide
                    evaluator={editingOwnAccount && !mfaSettings.activeMfaType}
                    show={<Button
                        text="Setup Multi-Factor Authentication"
                        onClick={() => setIsSettingUpMfa(true)}
                        disabled={saving || !!mfaSettings.activeMfaType}
                        loading={loading}
                        intent={Intent.PRIMARY}
                    />}
                />
            </div>
            <AlertConfirm
                isOpen={isResettingMfa}
                onCancel={() => setIsResettingMfa(false)}
                onConfirm={resetMfa}
                title="Reset MFA"
                confirmButtonText="Reset"
                confirmButtonIntent={Intent.DANGER}
            >
                Are you sure you want to reset MFA for {editingOwnAccount ? "your account" : "this user"}? {mfaSettings.mfaEnforced ? warningWhenResettingMfaOnEnforcedAccount : warningWhenResettingMfaOnUndenforcedAccount}
            </AlertConfirm>
            <AlertConfirm
                isOpen={isSettingUpMfa}
                onCancel={() => setIsSettingUpMfa(false)}
                onConfirm={setupMfa}
                title="Setup MFA"
                confirmButtonText="Continue"
                confirmButtonIntent={Intent.PRIMARY}
            >
                You are about to setup MFA for your account. This will require you logging in again and then walking through the MFA setup process. Click continue to proceed with setting up MFA.
            </AlertConfirm>
        </div>
    );
}

UserMfaSettings.propTypes = {
    userId: PropTypes.number.isRequired,
    editingOwnAccount: PropTypes.bool
}